import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    ActivityIndicator,
    FlatList,
    Text,
    View,
    RefreshControl,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { styles } from './styles';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import TemporaryPasswordBanner from '../../components/TemporaryPasswordBanner/TemporaryPasswordBanner';
import { useUserStore } from '../../store/UserStore';
import { AppRoutes } from '../../navigation/Routes.types';
import { Colors } from '../../constants/Colors';
import EquipamentsService from '../../services/EquipamentsService/EquipamentsService';
import { checkDigitalGate } from '../../services/DigitalGateService/DigitalGateService';
import { useLimitStore } from '../../store/LimitStore';
import { NavigationProps } from '../../navigation/Routes';

const RequestEquipmentList = () => {
    const navigation = useNavigation<NavigationProps>();
    const { goBack, navigate } = navigation;
    const [loadingItem, setLoadingItem] = useState<number|string|null>(null);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [error, setError] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const limitDigital = useLimitStore(state => state.digital);
    const [digitalEnabled, setDigitalEnabled] = useState<boolean | null>(limitDigital ?? null);
    const [digitalGateLoading, setDigitalGateLoading] = useState<boolean>(false);
    const [digitalGateError, setDigitalGateError] = useState<string | null>(null);
    const digitalStatusRef = useRef<boolean | null>(limitDigital ?? null);
    const isDigitalClient = digitalEnabled === true;
    const isLoading = loading || digitalGateLoading;
    const temporaryPasswordLogin = useUserStore(state => state.temporaryPasswordLogin);

    useEffect(() => {
        const nextValue = limitDigital ?? null;
        digitalStatusRef.current = nextValue;
        setDigitalEnabled(nextValue);
    }, [limitDigital]);

    const refreshDigitalGate = useCallback(async () => {
        setDigitalGateLoading(true);
        setDigitalGateError(null);
        try {
            const result = await checkDigitalGate();
            const value = result.value ?? null;
            digitalStatusRef.current = value;
            setDigitalEnabled(value);
            setDigitalGateError(result.error ?? null);
            return value;
        } finally {
            setDigitalGateLoading(false);
        }
    }, []);

    const handleNavigation = async () => await navigate(AppRoutes.REQUEST_EQUIPAMENT)

    const fetchData = useCallback(async (status?: boolean | null) => {
        const allowFetch = (status ?? digitalStatusRef.current) === true;

        if (!allowFetch) {
            setLoading(false);
            setRefreshing(false);
            setData([]);
            setError('');
            return;
        }

        setLoading(true);
        setRefreshing(true);
        try {
            const {data: response} = await EquipamentsService.RequestEquipmentList()
            setData(response?.data ?? []);
            setError('');
        } catch(e) {
            console.log('erro ao recuperar lista de equipamentos', e)
            setError('Não foi possível carregar a lista de equipamentos. Tente novamente.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);
    
    const updateRequest = async (id: number) => {
        setLoadingItem(id);
        try {
            const result = await EquipamentsService.UpdateEquipmentRequestStatus(id, {status_id: 3})
            fetchData()
        } catch(e) {
            console.log('erro ao atualizar o status', e)
        } finally {
            setLoadingItem(null);
        }
    }

    useFocusEffect(
        useCallback(() => {
            let isActive = true;

            const load = async () => {
                const status = await refreshDigitalGate();
                if (!isActive) {
                    return;
                }
                await fetchData(status);
            };

            load();

            return () => {
                isActive = false;
            };
        }, [refreshDigitalGate, fetchData]),
    );

    const onRefresh = useCallback(async () => {
        setRefreshing(true);
        const status = await refreshDigitalGate();
        await fetchData(status);
    }, [refreshDigitalGate, fetchData]);

    const renderItem = ({ item }: {item:any}) => (
        <View style={styles.itemContainer}>
            <Text style={styles.itemText}>Pedido: {item.id}</Text>
            <Text style={styles.itemText}>Status: {item.status.status_name.charAt(0).toUpperCase() + item.status.status_name.slice(1)}</Text>
            <Text style={styles.itemText}>Criado em: {new Date(item.created_at).toLocaleDateString()}</Text>
            <Text style={styles.itemText}>Pedido: {item.equipment}</Text>
            {item.status.id === 5 && (<Text style={styles.filterText}>Equipamentos indisponíveis no momento, solicite novamente no futuro!</Text>)}
            {![2,3, 5].includes(item.status_id) && <ButtonComponent
                outlined
                title={'Cancelar Solicitação'}
                disabled={temporaryPasswordLogin}
                onPress={() => updateRequest(item.id)}
                isLoading={loadingItem === item.id}
            />}
        </View>
    );
    
    return (
        <View style={styles.container}>
            <View style={styles.containerHeader}>
                <SearchHeader onPress={goBack} title="Equipamentos Solicitados" />
                <Text style={styles.description}>
                    Aqui você pode solicitar e acompanhar seus pedidos de equipamentos de forma fácil e rápida!
                </Text>
            </View>
            <TemporaryPasswordBanner />
            {digitalEnabled === false && !digitalGateLoading && (
                <View style={styles.centerMessage}>
                    <View style={styles.containerMessage}>
                        <Text style={styles.messageText}>
                            Para solicitar equipamentos, entre em contato com seu vendedor.
                        </Text>
                    </View>
                </View>
            )}
            {isLoading && <ActivityIndicator size="large" color={Colors.RedPallete.red} />}
            {!isLoading && digitalGateError && digitalEnabled !== true && (
                <Text style={styles.errorText}>{digitalGateError}</Text>
            )}
            {!isLoading && !digitalGateError && error && <Text style={styles.errorText}>{error}</Text>}
            {!error && !isLoading && isDigitalClient && (
                <FlatList
                    data={data}
                    renderItem={renderItem}
                    keyExtractor={(item) => item.id.toString()}
                    refreshControl={
                        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
                    }
                />
            )}
            

            {isDigitalClient && (
                <View style={styles.containerFooter}>
                    <ButtonComponent
                        title='Solicitar equipamentos'
                        disabled={temporaryPasswordLogin}
                        onPress={handleNavigation}
                    />
                </View>
            )}
        </View>
    )
}

export default RequestEquipmentList